/**
 * 设置页面 - API 配置
 *
 * 功能：
 * 1. 配置 LLM API 端点（支持自定义）
 * 2. 动态获取可用模型列表
 * 3. 配置各平台 API Key
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings as SettingsIcon,
  Key,
  Check,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  Globe,
  Server,
  RefreshCw,
} from 'lucide-react';
import { DEFAULT_API_URL, ENDPOINT_KEY } from '../lib/api';

// LLM 后端配置
const LLM_BACKENDS = [
  {
    id: 'openai-compatible',
    name: 'OpenAI 兼容',
    description: 'Ollama / vLLM / 本地模型',
    defaultBaseUrl: 'http://localhost:11434/v1',
    defaultModel: '',
    needsApiKey: false,
  },
  {
    id: 'glm',
    name: '智谱 GLM',
    description: '性价比高，中文能力强',
    defaultBaseUrl: 'https://open.bigmodel.cn/api/paas/v4',
    defaultModel: 'glm-4-flash',
    needsApiKey: true,
  },
  {
    id: 'minimax',
    name: 'MiniMax',
    description: '编程能力强，推理出色',
    defaultBaseUrl: 'https://api.minimaxi.com/v1/chat/completions',
    defaultModel: 'abab6.5s-chat',
    needsApiKey: true,
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: '综合能力强，全球领先',
    defaultBaseUrl: 'https://api.openai.com/v1',
    defaultModel: 'gpt-4o-mini',
    needsApiKey: true,
  },
] as const;

type BackendId = typeof LLM_BACKENDS[number]['id'];

interface SettingsState {
  apiEndpoint: string;
  apiKeys: Record<BackendId, string>;
  baseUrls: Record<BackendId, string>;
  models: Record<BackendId, string>;
  defaultBackend: BackendId;
}

// 本地存储键
const STORAGE_KEY = 'novel-outline-settings';

// 默认设置
const defaultSettings: SettingsState = {
  apiEndpoint: DEFAULT_API_URL,
  apiKeys: {
    'openai-compatible': '',
    openai: '',
    glm: '',
    minimax: '',
  },
  baseUrls: {
    'openai-compatible': 'http://localhost:11434/v1',
    openai: 'https://api.openai.com/v1',
    glm: 'https://open.bigmodel.cn/api/paas/v4',
    minimax: 'https://api.minimaxi.com/v1/chat/completions',
  },
  models: {
    'openai-compatible': '',
    openai: 'gpt-4o-mini',
    glm: 'glm-4-flash',
    minimax: 'MiniMax-M2.5',
  },
  defaultBackend: 'glm',
};

export default function Settings() {
  const [settings, setSettings] = useState<SettingsState>(defaultSettings);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [testingBackend, setTestingBackend] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, 'success' | 'error' | null>>({});
  const [testMessages, setTestMessages] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [endpointTesting, setEndpointTesting] = useState(false);
  const [endpointStatus, setEndpointStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // 动态模型列表
  const [availableModels, setAvailableModels] = useState<Record<BackendId, { id: string; name: string }[]>>({
    'openai-compatible': [],
    openai: [],
    glm: [],
    minimax: [],
  });
  const [loadingModels, setLoadingModels] = useState<Record<BackendId, boolean>>({
    'openai-compatible': false,
    openai: false,
    glm: false,
    minimax: false,
  });

  // 加载设置
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings({
          ...defaultSettings,
          ...parsed,
          apiKeys: { ...defaultSettings.apiKeys, ...parsed.apiKeys },
          baseUrls: { ...defaultSettings.baseUrls, ...parsed.baseUrls },
          models: { ...defaultSettings.models, ...parsed.models },
        });
      } catch (e) {
        console.error('Failed to parse settings:', e);
      }
    }
  }, []);

  // 保存设置
  const saveSettings = async (newSettings: SettingsState) => {
    setSaveStatus('saving');
    setSettings(newSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
    await new Promise(resolve => setTimeout(resolve, 300));
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
    localStorage.setItem(ENDPOINT_KEY, newSettings.apiEndpoint);
  };

  // 更新后端服务端点
  const updateEndpoint = async (endpoint: string) => {
    const newSettings = { ...settings, apiEndpoint: endpoint };
    setEndpointTesting(true);
    setEndpointStatus('idle');

    try {
      const response = await fetch(`${endpoint}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      });
      if (response.ok) {
        setEndpointStatus('success');
        saveSettings(newSettings);
      } else {
        setEndpointStatus('error');
      }
    } catch {
      setEndpointStatus('error');
    } finally {
      setEndpointTesting(false);
    }
  };

  // 更新 LLM API 端点
  const updateBaseUrl = (backend: BackendId, url: string) => {
    saveSettings({
      ...settings,
      baseUrls: { ...settings.baseUrls, [backend]: url },
    });
    // 清除测试结果和模型列表
    setTestResults(prev => ({ ...prev, [backend]: null }));
    setTestMessages(prev => ({ ...prev, [backend]: '' }));
    setAvailableModels(prev => ({ ...prev, [backend]: [] }));
  };

  // 更新 API Key
  const updateApiKey = (backend: BackendId, key: string) => {
    saveSettings({
      ...settings,
      apiKeys: { ...settings.apiKeys, [backend]: key },
    });
    setTestResults(prev => ({ ...prev, [backend]: null }));
    setTestMessages(prev => ({ ...prev, [backend]: '' }));
  };

  // 更新模型选择
  const updateModel = (backend: BackendId, model: string) => {
    saveSettings({
      ...settings,
      models: { ...settings.models, [backend]: model },
    });
  };

  // 更新默认后端
  const updateDefaultBackend = (backend: BackendId) => {
    saveSettings({ ...settings, defaultBackend: backend });
  };

  // 获取可用模型列表
  const fetchModels = async (backendId: BackendId) => {
    const apiKey = settings.apiKeys[backendId];
    const baseUrl = settings.baseUrls[backendId];

    if (!baseUrl.trim()) {
      setTestMessages(prev => ({ ...prev, [backendId]: '请先输入 API 端点' }));
      return;
    }

    setLoadingModels(prev => ({ ...prev, [backendId]: true }));

    try {
      const response = await fetch(`${settings.apiEndpoint}/api/config/models`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          backend: backendId,
          apiKey: apiKey || '',
          baseUrl,
        }),
      });

      const result = await response.json();

      if (result.success && result.models) {
        setAvailableModels(prev => ({ ...prev, [backendId]: result.models }));
        // 如果当前没有选择模型，自动选择第一个
        if (!settings.models[backendId] && result.models.length > 0) {
          updateModel(backendId, result.models[0].id);
        }
        setTestMessages(prev => ({ ...prev, [backendId]: `获取到 ${result.models.length} 个模型` }));
      } else {
        setTestMessages(prev => ({ ...prev, [backendId]: result.error || '获取失败' }));
      }
    } catch (error) {
      console.error('Fetch models failed:', error);
      setTestMessages(prev => ({ ...prev, [backendId]: '网络错误' }));
    } finally {
      setLoadingModels(prev => ({ ...prev, [backendId]: false }));
    }
  };

  // 测试 LLM API 连接
  const testConnection = async (backendId: BackendId) => {
    const apiKey = settings.apiKeys[backendId];
    const baseUrl = settings.baseUrls[backendId];
    const model = settings.models[backendId];

    const backend = LLM_BACKENDS.find(b => b.id === backendId);

    // 检查必要条件
    if (!baseUrl.trim()) {
      setTestResults(prev => ({ ...prev, [backendId]: 'error' }));
      setTestMessages(prev => ({ ...prev, [backendId]: '请输入 API 端点' }));
      return;
    }

    if (backend?.needsApiKey && !apiKey.trim()) {
      setTestResults(prev => ({ ...prev, [backendId]: 'error' }));
      setTestMessages(prev => ({ ...prev, [backendId]: '请输入 API Key' }));
      return;
    }

    setTestingBackend(backendId);
    setTestResults(prev => ({ ...prev, [backendId]: null }));
    setTestMessages(prev => ({ ...prev, [backendId]: '' }));

    try {
      const response = await fetch(`${settings.apiEndpoint}/api/config/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          backend: backendId,
          apiKey: apiKey || 'no-key',
          baseUrl,
          model: model || 'default'
        }),
      });

      const result = await response.json();
      setTestResults(prev => ({
        ...prev,
        [backendId]: result.success ? 'success' : 'error'
      }));
      setTestMessages(prev => ({
        ...prev,
        [backendId]: result.message || result.error || ''
      }));
    } catch (error) {
      console.error('Test failed:', error);
      setTestResults(prev => ({ ...prev, [backendId]: 'error' }));
      setTestMessages(prev => ({ ...prev, [backendId]: '网络错误，请检查连接' }));
    } finally {
      setTestingBackend(null);
    }
  };

  // 切换密钥可见性
  const toggleShowKey = (backend: string) => {
    setShowKeys(prev => ({ ...prev, [backend]: !prev[backend] }));
  };

  // 检查后端是否已配置
  const isBackendConfigured = (backendId: BackendId): boolean => {
    const backend = LLM_BACKENDS.find(b => b.id === backendId);
    const hasUrl = settings.baseUrls[backendId].trim().length > 0;
    if (!backend?.needsApiKey) {
      return hasUrl;
    }
    return hasUrl && settings.apiKeys[backendId].trim().length > 0;
  };

  // 获取模型选项（动态 + 手动输入）
  const getModelOptions = (backendId: BackendId) => {
    const dynamic = availableModels[backendId] || [];
    const currentModel = settings.models[backendId];

    // 如果当前模型不在列表中，添加它
    if (currentModel && !dynamic.find(m => m.id === currentModel)) {
      return [...dynamic, { id: currentModel, name: `${currentModel} (手动)` }];
    }
    return dynamic;
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* 标题 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">设置</h1>
              <p className="text-sm text-slate-500">配置 LLM API 端点和模型</p>
            </div>
          </div>
          {saveStatus === 'saved' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg"
            >
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">已保存</span>
            </motion.div>
          )}
        </div>

        {/* 后端服务端点 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-5 h-5 text-slate-600" />
            <h2 className="font-semibold text-slate-900">后端服务</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            大纲生成服务的地址，一般无需修改。
          </p>

          <div className="flex gap-2">
            <input
              type="url"
              value={settings.apiEndpoint}
              onChange={(e) => setSettings({ ...settings, apiEndpoint: e.target.value })}
              onBlur={(e) => updateEndpoint(e.target.value)}
              className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
            <button
              onClick={() => updateEndpoint(settings.apiEndpoint)}
              disabled={endpointTesting}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                endpointStatus === 'success'
                  ? 'bg-green-500 text-white'
                  : endpointStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {endpointTesting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : endpointStatus === 'success' ? (
                <Check className="w-4 h-4" />
              ) : endpointStatus === 'error' ? (
                <AlertCircle className="w-4 h-4" />
              ) : null}
              {endpointTesting ? '测试中' : endpointStatus === 'success' ? '已连接' : endpointStatus === 'error' ? '失败' : '测试'}
            </button>
          </div>
        </div>

        {/* 默认模型选择 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4">默认 AI 模型</h2>

          <div className="grid gap-3">
            {LLM_BACKENDS.map((backend) => {
              const isSelected = settings.defaultBackend === backend.id;
              const isConfigured = isBackendConfigured(backend.id);

              return (
                <button
                  key={backend.id}
                  onClick={() => isConfigured && updateDefaultBackend(backend.id)}
                  disabled={!isConfigured}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50'
                      : isConfigured
                        ? 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        : 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-primary-500 text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <span className="font-bold text-sm">{backend.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{backend.name}</span>
                      {!isConfigured && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          未配置
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{backend.description}</p>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* API 配置详情 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-5 h-5 text-slate-600" />
            <h2 className="font-semibold text-slate-900">API 配置详情</h2>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            配置各模型的 API 端点和密钥。点击"获取模型"从服务端拉取可用模型列表。
          </p>

          <div className="space-y-8">
            {LLM_BACKENDS.map((backend) => {
              const apiKey = settings.apiKeys[backend.id];
              const baseUrl = settings.baseUrls[backend.id];
              const selectedModel = settings.models[backend.id];
              const isShowing = showKeys[backend.id];
              const isTesting = testingBackend === backend.id;
              const testResult = testResults[backend.id];
              const testMessage = testMessages[backend.id];
              const isLoadingModels = loadingModels[backend.id];
              const modelOptions = getModelOptions(backend.id);

              return (
                <div key={backend.id} className="space-y-3 pb-6 border-b border-slate-100 last:border-0 last:pb-0">
                  {/* 后端标题 */}
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{backend.name}</span>
                    <span className="text-xs text-slate-400">{backend.description}</span>
                  </div>

                  {/* API 端点 */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">API 端点</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="url"
                          value={baseUrl}
                          onChange={(e) => updateBaseUrl(backend.id, e.target.value)}
                          placeholder={backend.defaultBaseUrl}
                          className="w-full px-4 py-2.5 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                        />
                        <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>

                  {/* API Key（如果需要）*/}
                  {backend.needsApiKey && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">API Key</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type={isShowing ? 'text' : 'password'}
                            value={apiKey}
                            onChange={(e) => updateApiKey(backend.id, e.target.value)}
                            placeholder={`输入 ${backend.name} API Key`}
                            className="w-full px-4 py-2.5 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => toggleShowKey(backend.id)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {isShowing ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 模型选择 */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">模型</label>
                    <div className="flex gap-2">
                      {modelOptions.length > 0 ? (
                        <select
                          value={selectedModel}
                          onChange={(e) => updateModel(backend.id, e.target.value)}
                          className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">选择模型...</option>
                          {modelOptions.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={selectedModel}
                          onChange={(e) => updateModel(backend.id, e.target.value)}
                          placeholder="输入模型名称或点击获取"
                          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                        />
                      )}
                      <button
                        onClick={() => fetchModels(backend.id)}
                        disabled={isLoadingModels || !baseUrl.trim()}
                        className="flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                        title="从服务端获取模型列表"
                      >
                        {isLoadingModels ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        获取
                      </button>
                    </div>
                  </div>

                  {/* 测试按钮和结果 */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => testConnection(backend.id)}
                      disabled={isTesting || !baseUrl.trim()}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        testResult === 'success'
                          ? 'bg-green-500 text-white'
                          : testResult === 'error'
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50'
                      }`}
                    >
                      {isTesting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : testResult === 'success' ? (
                        <Check className="w-4 h-4" />
                      ) : testResult === 'error' ? (
                        <AlertCircle className="w-4 h-4" />
                      ) : null}
                      {isTesting ? '测试中...' : testResult === 'success' ? '连接成功' : testResult === 'error' ? '连接失败' : '测试连接'}
                    </button>
                    {testMessage && (
                      <span className={`text-xs ${testResult === 'success' ? 'text-green-600' : testResult === 'error' ? 'text-red-500' : 'text-slate-500'}`}>
                        {testMessage}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 帮助提示 */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-sm text-blue-800 font-medium mb-2">使用说明</p>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Ollama</strong>：端点 http://localhost:11434/v1，无需 API Key</li>
            <li>• <strong>LM Studio</strong>：端点 http://localhost:1234/v1，无需 API Key</li>
            <li>• <strong>vLLM</strong>：端点 http://localhost:8000/v1，无需 API Key</li>
            <li>• 点击 <strong>"获取"</strong> 按钮可从服务端拉取可用模型列表</li>
            <li>• 模型列表为空时可手动输入模型名称</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}
